import { useEffect, useState } from "react";
import {
  Wallet,
  Users,
  Star,
  TrendingUp,
  PiggyBank,
  ShieldAlert,
  UserCheck,
  Camera,
  Clock,
  ChevronDown,
  ArrowRight,
  BarChart3,
  Sparkles,
  CalendarRange,
  Building2,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { hu } from "date-fns/locale";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Line,
  LineChart,
  Area,
  AreaChart,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const fmtFt = (v) => `${Math.round(v || 0).toLocaleString("hu-HU")} Ft`;
const fmtHu = (n) => Number(n || 0).toLocaleString("hu-HU");
const fmtCompact = (v) => {
  const n = Math.round(v || 0);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n}`;
};

const HU_MONTHS = [
  "január",
  "február",
  "március",
  "április",
  "május",
  "június",
  "július",
  "augusztus",
  "szeptember",
  "október",
  "november",
  "december",
];

const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

function defaultRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start: toIso(first), end: toIso(now) };
}

const PRESETS = [
  { id: "today", label: "Ma" },
  { id: "yesterday", label: "Tegnap" },
  { id: "this_month", label: "Aktuális hónap" },
  { id: "last_month", label: "Előző hónap" },
  { id: "last_30", label: "Utolsó 30 nap" },
  { id: "last_90", label: "Utolsó 90 nap" },
  { id: "ytd", label: "Idei év" },
  { id: "last_year", label: "Tavalyi év" },
];

function presetRange(id) {
  const now = new Date();
  if (id === "today") {
    return { start: toIso(now), end: toIso(now) };
  }
  if (id === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return { start: toIso(y), end: toIso(y) };
  }
  if (id === "this_month") {
    return {
      start: toIso(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: toIso(now),
    };
  }
  if (id === "last_month") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: toIso(first), end: toIso(last) };
  }
  if (id === "last_30") {
    const s = new Date(now);
    s.setDate(s.getDate() - 29);
    return { start: toIso(s), end: toIso(now) };
  }
  if (id === "last_90") {
    const s = new Date(now);
    s.setDate(s.getDate() - 89);
    return { start: toIso(s), end: toIso(now) };
  }
  if (id === "ytd") {
    return {
      start: toIso(new Date(now.getFullYear(), 0, 1)),
      end: toIso(now),
    };
  }
  if (id === "last_year") {
    return {
      start: toIso(new Date(now.getFullYear() - 1, 0, 1)),
      end: toIso(new Date(now.getFullYear() - 1, 11, 31)),
    };
  }
  return defaultRange();
}

function rangeLabel(range, activePreset) {
  if (activePreset) {
    const p = PRESETS.find((x) => x.id === activePreset);
    if (p) return p.label;
  }
  const s = new Date(range.start);
  const e = new Date(range.end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sFmt = `${s.getFullYear()}. ${HU_MONTHS[s.getMonth()]} ${s.getDate()}.`;
  const eFmt = sameYear
    ? `${HU_MONTHS[e.getMonth()]} ${e.getDate()}.`
    : `${e.getFullYear()}. ${HU_MONTHS[e.getMonth()]} ${e.getDate()}.`;
  return `${sFmt} – ${eFmt}`;
}

export default function StatisticsPage() {
  const initial = defaultRange();
  const [range, setRange] = useState(initial);
  const [activePreset, setActivePreset] = useState("this_month");
  const [data, setData] = useState(null);
  const [scheduleStats, setScheduleStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async (r) => {
    setLoading(true);
    try {
      const [overview, sched] = await Promise.all([
        api.get("/statistics/overview", {
          params: { start_date: r.start, end_date: r.end },
        }),
        api.get("/statistics/schedule", {
          params: { start_date: r.start, end_date: r.end },
        }),
      ]);
      setData(overview.data);
      setScheduleStats(sched.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Adatok betöltése sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(range);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (id) => {
    const r = presetRange(id);
    setActivePreset(id);
    setRange(r);
    fetchStats(r);
  };

  const applyManual = (next) => {
    setActivePreset(null);
    setRange(next);
    fetchStats(next);
  };

  return (
    <div className="space-y-6 md:space-y-8" data-testid="statistics-page">
      {/* Hero */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-white via-slate-50/60 to-indigo-50/40 px-4 py-5 dark:border-border dark:from-card dark:via-card dark:to-indigo-950/20 sm:px-6 sm:py-6 md:px-8 md:py-7">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-indigo-400/10 blur-3xl"
        />
        <div className="relative flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-foreground/[0.04] px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <BarChart3 className="h-3 w-3" strokeWidth={2.2} />
              Statisztika
            </div>
            <h1 className="mt-2.5 text-[22px] font-semibold leading-tight tracking-tight sm:text-[26px] md:text-[32px]">
              Üzleti áttekintés
            </h1>
            {data?.period && (
              <p className="mt-1.5 text-[12px] text-slate-500 sm:text-[13px]">
                {rangeLabel(range, activePreset)}
                <span className="mx-2 text-slate-300">·</span>
                <span className="tabular-nums">
                  {data.period.days} nap
                </span>
                <span className="mx-2 text-slate-300">·</span>
                {data.period.bucket_mode === "day"
                  ? "napi bontás"
                  : "havi bontás"}
              </p>
            )}
          </div>
          <PeriodPicker
            range={range}
            activePreset={activePreset}
            label={rangeLabel(range, activePreset)}
            onPreset={applyPreset}
            onManual={applyManual}
          />
        </div>
      </header>

      <Tabs defaultValue="finance" className="space-y-6">
        <TabsList
          className="h-11 rounded-2xl border border-slate-200/80 bg-white p-1 shadow-[0_2px_10px_-8px_rgba(15,23,42,0.18)] dark:border-border dark:bg-card"
          data-testid="statistics-tabs"
        >
          <TabsTrigger
            value="finance"
            data-testid="tab-finance"
            className="gap-1.5 rounded-xl px-3 text-[12px] data-[state=active]:bg-foreground data-[state=active]:text-background sm:px-4 sm:text-[12.5px]"
          >
            <Wallet className="h-3.5 w-3.5" /> Pénzügyek
          </TabsTrigger>
          <TabsTrigger
            value="hr"
            data-testid="tab-hr"
            className="gap-1.5 rounded-xl px-3 text-[12px] data-[state=active]:bg-foreground data-[state=active]:text-background sm:px-4 sm:text-[12.5px]"
          >
            <Users className="h-3.5 w-3.5" /> HR
          </TabsTrigger>
          <TabsTrigger
            value="social"
            data-testid="tab-social"
            className="gap-1.5 rounded-xl px-3 text-[12px] data-[state=active]:bg-foreground data-[state=active]:text-background sm:px-4 sm:text-[12.5px]"
          >
            <Star className="h-3.5 w-3.5" /> Social
          </TabsTrigger>
          <TabsTrigger
            value="schedule"
            data-testid="tab-schedule"
            className="gap-1.5 rounded-xl px-3 text-[12px] data-[state=active]:bg-foreground data-[state=active]:text-background sm:px-4 sm:text-[12.5px]"
          >
            <CalendarRange className="h-3.5 w-3.5" /> Beosztás
          </TabsTrigger>
        </TabsList>

        <TabsContent value="finance" className="space-y-6">
          <FinanceTab data={data?.finance} period={data?.period} loading={loading} />
        </TabsContent>
        <TabsContent value="hr" className="space-y-6">
          <HRTab data={data?.hr} period={data?.period} loading={loading} />
        </TabsContent>
        <TabsContent value="social" className="space-y-6">
          <SocialTab data={data?.social} period={data?.period} loading={loading} />
        </TabsContent>
        <TabsContent value="schedule" className="space-y-6">
          <ScheduleTab data={scheduleStats} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Period picker (premium, range-aware calendar + quick chips) ----------
const toJsDate = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

function fmtShort(iso) {
  if (!iso) return "—";
  const d = toJsDate(iso);
  return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}. ${String(d.getDate()).padStart(2, "0")}.`;
}

function PeriodPicker({ range, activePreset, onPreset, onManual }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    from: toJsDate(range.start),
    to: toJsDate(range.end),
  });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    setDraft({ from: toJsDate(range.start), to: toJsDate(range.end) });
  }, [range.start, range.end]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const apply = () => {
    if (!draft.from || !draft.to) return;
    onManual({ start: toIso(draft.from), end: toIso(draft.to) });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="statistics-period-trigger"
          className="group relative inline-flex h-12 w-full items-center gap-3 overflow-hidden rounded-2xl border border-slate-200/70 bg-white pl-2 pr-3.5 text-left shadow-[0_8px_24px_-18px_rgba(15,23,42,0.32)] transition hover:border-slate-300 hover:shadow-[0_12px_30px_-18px_rgba(15,23,42,0.36)] dark:border-border dark:bg-card sm:w-auto"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-50/40 via-transparent to-fuchsia-50/40 opacity-0 transition-opacity group-hover:opacity-100"
          />
          <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-[0_6px_16px_-8px_rgba(99,102,241,0.55)]">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.2} />
          </span>
          <span className="relative flex min-w-0 flex-col leading-tight">
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Időszak
            </span>
            <span className="truncate font-mono text-[12px] font-medium tabular-nums tracking-tight text-slate-800 dark:text-foreground sm:text-[12.5px]">
              {fmtShort(range.start)}
              <span className="mx-1.5 text-slate-300">→</span>
              {fmtShort(range.end)}
            </span>
          </span>
          <ChevronDown
            className="relative ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform group-data-[state=open]:rotate-180 sm:ml-1"
            strokeWidth={1.8}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[calc(100vw-1.5rem)] max-w-[640px] rounded-3xl border-slate-200/80 p-0 shadow-[0_28px_72px_-24px_rgba(15,23,42,0.45)] sm:w-auto"
        data-testid="statistics-period-popover"
      >
        {/* Premium header: live range + quick chips */}
        <div className="border-b border-slate-200/70 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 px-5 py-4 dark:border-border dark:from-card dark:via-card dark:to-indigo-950/10">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Kiválasztott időszak
              </p>
              <div className="mt-1 flex items-baseline gap-2 font-mono text-[14px] font-medium tabular-nums tracking-tight">
                <span data-testid="popover-draft-start">
                  {draft.from ? fmtShort(toIso(draft.from)) : "—"}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                <span data-testid="popover-draft-end">
                  {draft.to ? fmtShort(toIso(draft.to)) : "—"}
                </span>
              </div>
            </div>
            {draft.from && draft.to && (
              <div className="rounded-xl bg-foreground/[0.05] px-2.5 py-1 text-right">
                <p className="font-mono text-[15px] font-light leading-none tabular-nums">
                  {Math.round(
                    (draft.to - draft.from) / 86_400_000
                  ) + 1}
                </p>
                <p className="text-[9.5px] uppercase tracking-[0.16em] text-slate-400">
                  nap
                </p>
              </div>
            )}
          </div>
          <div className="mt-3.5 flex flex-wrap gap-1.5">
            {PRESETS.map((p) => {
              const active = activePreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`statistics-preset-${p.id}`}
                  onClick={() => {
                    onPreset(p.id);
                    setOpen(false);
                  }}
                  className={
                    "rounded-full px-3 py-1 text-[11.5px] font-medium transition " +
                    (active
                      ? "bg-foreground text-background shadow-[0_4px_12px_-6px_rgba(15,23,42,0.4)]"
                      : "border border-slate-200/80 bg-white text-slate-600 hover:border-foreground hover:text-foreground dark:border-border dark:bg-foreground/[0.04] dark:text-foreground/70")
                  }
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Range calendar */}
        <div className="p-2">
          <Calendar
            mode="range"
            locale={hu}
            numberOfMonths={isMobile ? 1 : 2}
            selected={draft}
            onSelect={(v) => setDraft(v || { from: undefined, to: undefined })}
            defaultMonth={draft.from || new Date()}
            weekStartsOn={1}
            className="rdp-statistics"
            classNames={{
              months:
                "flex flex-col gap-3 sm:flex-row sm:gap-6 px-3 pt-2 pb-1",
              caption: "flex items-center justify-center pt-1 relative",
              caption_label:
                "text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-700 dark:text-foreground",
              nav: "flex items-center gap-1",
              nav_button:
                "h-7 w-7 grid place-items-center rounded-lg border border-slate-200/80 bg-white text-slate-500 hover:border-foreground hover:text-foreground transition dark:border-border dark:bg-foreground/[0.04]",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              head_cell:
                "w-9 text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400",
              cell: "h-9 w-9 text-center p-0 relative [&:has([aria-selected])]:bg-indigo-500/10 [&:has([aria-selected].day-range-end)]:rounded-r-full [&:has([aria-selected].day-range-start)]:rounded-l-full",
              day: "h-9 w-9 inline-flex items-center justify-center rounded-full text-[12px] font-medium tabular-nums hover:bg-foreground/[0.06] focus:outline-none",
              day_selected:
                "bg-foreground text-background hover:bg-foreground hover:text-background",
              day_range_start: "day-range-start bg-foreground text-background",
              day_range_end: "day-range-end bg-foreground text-background",
              day_range_middle:
                "bg-indigo-500/15 text-foreground hover:bg-indigo-500/20",
              day_today:
                "ring-1 ring-foreground/30 ring-inset font-semibold",
              day_outside: "text-slate-300",
              day_disabled: "opacity-40",
              day_hidden: "invisible",
            }}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200/70 bg-slate-50/40 px-5 py-3 dark:border-border dark:bg-foreground/[0.02]">
          <p className="text-[11px] text-slate-400">
            Kattints egy kezdő- és záró napra
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="statistics-cancel-range"
              onClick={() => setOpen(false)}
              className="rounded-xl px-3 py-1.5 text-[12px] font-medium text-slate-500 hover:bg-foreground/[0.06] hover:text-foreground"
            >
              Mégse
            </button>
            <button
              type="button"
              data-testid="statistics-apply-range"
              disabled={!draft.from || !draft.to}
              onClick={apply}
              className="inline-flex items-center gap-1.5 rounded-xl bg-foreground px-3.5 py-1.5 text-[12px] font-medium text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Alkalmaz <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------- Reusable UI ----------
function KpiTile({ label, value, sub, icon: Icon, accent = "#6366f1" }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_4px_18px_-14px_rgba(15,23,42,0.18)] dark:border-border dark:bg-card">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>
        {Icon && (
          <div
            className="grid h-7 w-7 place-items-center rounded-lg"
            style={{ background: `${accent}15`, color: accent }}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </div>
        )}
      </div>
      <p className="mt-2 font-mono text-[22px] font-light leading-none tabular-nums tracking-tight">
        {value}
      </p>
      {sub && <p className="mt-1.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  icon: Icon,
  accent = "#6366f1",
  action,
  children,
  full,
}) {
  return (
    <div
      className={
        "rounded-3xl border border-slate-200/70 bg-white p-5 shadow-[0_8px_30px_-22px_rgba(15,23,42,0.18)] dark:border-border dark:bg-card md:p-6 " +
        (full ? "col-span-full " : "")
      }
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div
              className="grid h-9 w-9 place-items-center rounded-xl"
              style={{ background: `${accent}15`, color: accent }}
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
            </div>
          )}
          <div>
            <h3 className="text-[13.5px] font-semibold tracking-tight text-slate-800 dark:text-foreground">
              {title}
            </h3>
            {subtitle && (
              <p className="text-[11.5px] text-slate-400">{subtitle}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyChart({ note = "Nincs adat a kiválasztott időszakra." }) {
  return (
    <div className="grid h-[220px] place-items-center rounded-2xl border border-dashed border-slate-200/80 bg-slate-50/40 dark:border-border dark:bg-foreground/[0.02]">
      <p className="text-[12px] text-slate-400">{note}</p>
    </div>
  );
}

// ---------- Charts ----------
function RevenueAreaChart({ data, height = 280 }) {
  if (!data?.length || data.every((d) => !d.total)) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity={0.28} />
            <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="rgba(15,23,42,0.06)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={48}
          tickFormatter={fmtCompact}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            fontSize: 12,
            boxShadow: "0 12px 30px -10px rgba(15,23,42,0.2)",
          }}
          formatter={(v) => fmtFt(v)}
          labelStyle={{ color: "#64748b", fontWeight: 500 }}
        />
        <Area
          type="monotone"
          dataKey="total"
          stroke="#10b981"
          strokeWidth={2.5}
          fill="url(#revGrad)"
          dot={false}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function HoursLineChart({ data, height = 260 }) {
  if (!data?.length || data.every((d) => !d.hours)) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="rgba(15,23,42,0.06)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={42}
          tickFormatter={(v) => `${v}h`}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            fontSize: 12,
            boxShadow: "0 12px 30px -10px rgba(15,23,42,0.2)",
          }}
          formatter={(v) => `${v} óra`}
          labelStyle={{ color: "#64748b", fontWeight: 500 }}
        />
        <Line
          type="monotone"
          dataKey="hours"
          stroke="#6366f1"
          strokeWidth={2.5}
          dot={{ r: 2.5, fill: "#6366f1", stroke: "#fff", strokeWidth: 1.5 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function SimpleBarChart({ data, color = "#6366f1", height = 260, formatY }) {
  if (!data?.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="rgba(15,23,42,0.06)"
          vertical={false}
        />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={48}
          tickFormatter={formatY}
          allowDecimals={false}
        />
        <Tooltip
          cursor={{ fill: "rgba(99,102,241,0.06)" }}
          contentStyle={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            fontSize: 12,
            boxShadow: "0 12px 30px -10px rgba(15,23,42,0.2)",
          }}
          formatter={(v) => (formatY ? formatY(v) : v)}
        />
        <Bar dataKey="count" fill={color} radius={[8, 8, 0, 0]} maxBarSize={36} />
        <Bar dataKey="total" fill={color} radius={[8, 8, 0, 0]} maxBarSize={36} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------- Ranked list (replaces multi cards / awkward bar charts) ----------
function RankedList({ items, accent = "#6366f1", valueFormatter, secondary }) {
  if (!items?.length) return <EmptyChart />;
  const max = Math.max(...items.map((i) => i.total ?? i.count ?? 0), 1);
  return (
    <ul className="space-y-2.5">
      {items.map((item, idx) => {
        const v = item.total ?? item.count ?? 0;
        const pct = (v / max) * 100;
        return (
          <li
            key={`${item.name}-${idx}`}
            data-testid={`ranked-item-${idx}`}
            className="group"
          >
            <div className="mb-1 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-slate-100 font-mono text-[10.5px] font-semibold tabular-nums text-slate-500 dark:bg-foreground/[0.06]">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium leading-tight">
                    {item.name}
                  </p>
                  {(item.taj || item.site_name) && (
                    <p className="mt-0.5 truncate font-mono text-[10.5px] tabular-nums text-slate-400">
                      {item.taj && (
                        <span data-testid={`ranked-item-${idx}-taj`}>
                          TAJ {item.taj}
                        </span>
                      )}
                      {item.taj && item.site_name && (
                        <span className="text-slate-300"> · </span>
                      )}
                      {item.site_name && <span>{item.site_name}</span>}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className="font-mono text-[12.5px] font-semibold tabular-nums">
                  {valueFormatter ? valueFormatter(v) : v}
                </span>
                {secondary && item[secondary.key] !== undefined && (
                  <p className="text-[10.5px] text-slate-400">
                    {secondary.format(item[secondary.key])}
                  </p>
                )}
              </div>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-foreground/[0.06]">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${accent}66, ${accent})`,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------- FINANCE TAB ----------
function FinanceTab({ data, period, loading }) {
  if (loading) return <SkeletonGrid />;
  if (!data) return <Empty />;
  const bucketLabel = period?.bucket_mode === "day" ? "naponta" : "havonta";

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          label="Napi bevétel"
          value={fmtFt(data.revenues_total)}
          sub="időszak összesen"
          icon={TrendingUp}
          accent="#10b981"
        />
        <KpiTile
          label="Munkabér kifizetve"
          value={fmtFt(data.salaries_paid_total)}
          sub={`${fmtHu(data.salary_count)} tétel`}
          icon={Wallet}
          accent="#6366f1"
        />
        <KpiTile
          label="Bónusz / Jutalék"
          value={fmtFt(data.bonus_summary?.total_amount)}
          sub={`${fmtHu(data.bonus_summary?.count)} db kiosztva`}
          icon={Star}
          accent="#f59e0b"
        />
        <KpiTile
          label="Elutasított"
          value={fmtFt(data.bonus_summary?.rejected_amount)}
          sub="bónusz veszteség"
          icon={ShieldAlert}
          accent="#ef4444"
        />
      </div>

      {/* Daily revenues big area chart */}
      <ChartCard
        title="Napi bevételek"
        subtitle={`Bevétel ${bucketLabel} az időszakban`}
        icon={TrendingUp}
        accent="#10b981"
        full
      >
        <RevenueAreaChart data={data.revenues_series} />
      </ChartCard>

      {/* Two columns: Salary ranking + Top cash registers */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Munkabér kifizetések"
          subtitle="Dolgozónként a kifizetett bruttó összegek"
          icon={Wallet}
          accent="#6366f1"
        >
          <RankedList
            items={data.salary_by_employee}
            accent="#6366f1"
            valueFormatter={fmtFt}
          />
        </ChartCard>
        <ChartCard
          title="Legtöbbet használt házipénztárak"
          subtitle="Készpénzes kifizetések — Top 5"
          icon={PiggyBank}
          accent="#f59e0b"
        >
          <RankedList
            items={data.top_cash_registers}
            accent="#f59e0b"
            valueFormatter={(v) => `${fmtHu(v)} tranz.`}
            secondary={{ key: "total_amount", format: fmtFt }}
          />
        </ChartCard>
      </div>
    </div>
  );
}

// ---------- HR TAB ----------
function HRTab({ data, period, loading }) {
  if (loading) return <SkeletonGrid />;
  if (!data) return <Empty />;
  const bucketLabel = period?.bucket_mode === "day" ? "naponta" : "havonta";
  const onbTotal = (data.onboarding_breakdown || []).reduce(
    (sum, b) => sum + (b.value || 0),
    0
  );
  const onbDone =
    data.onboarding_breakdown?.find((b) => b.label === "Lezárva")?.value || 0;
  const onbPending =
    data.onboarding_breakdown?.find((b) => b.label === "Folyamatban")?.value ||
    0;
  const onbPct = onbTotal ? Math.round((onbDone / onbTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          label="Ledolgozott óra"
          value={`${fmtHu(
            Math.round((data.total_hours || 0) * 10) / 10
          )} h`}
          sub="beosztott műszakok"
          icon={Clock}
          accent="#6366f1"
        />
        <KpiTile
          label="Fegyelmi"
          value={fmtHu(data.disciplinary?.total)}
          sub="bejegyzés az időszakban"
          icon={ShieldAlert}
          accent="#f43f5e"
        />
        <KpiTile
          label="Elutasított bónusz"
          value={fmtFt(data.disciplinary?.lost_amount)}
          sub="fegyelmi miatt"
          icon={ShieldAlert}
          accent="#ef4444"
        />
        <KpiTile
          label="Onboarding"
          value={`${onbPct}%`}
          sub={`${fmtHu(onbDone)} / ${fmtHu(onbTotal)} aktív`}
          icon={UserCheck}
          accent="#10b981"
        />
      </div>

      <ChartCard
        title="Ledolgozott órák trendje"
        subtitle={`Beosztott műszakok órái ${bucketLabel}`}
        icon={Clock}
        accent="#6366f1"
        full
      >
        <HoursLineChart data={data.hours_series} />
      </ChartCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Onboarding áttekintés"
          subtitle="Aktív munkavállalók státusza"
          icon={UserCheck}
          accent="#10b981"
        >
          <OnboardingDial
            done={onbDone}
            pending={onbPending}
            total={onbTotal}
            pct={onbPct}
          />
        </ChartCard>
        <ChartCard
          title="Fegyelmi részletek"
          subtitle="Elutasított bér/bónusz miatt"
          icon={ShieldAlert}
          accent="#f43f5e"
        >
          <DisciplinaryDetail data={data.disciplinary} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Fegyelmi — telephelyenként"
          subtitle="Top 10 telephely"
          icon={ShieldAlert}
          accent="#f43f5e"
        >
          <RankedList
            items={data.disciplinary?.by_site || []}
            accent="#f43f5e"
            valueFormatter={(v) => `${fmtHu(v)} eset`}
          />
        </ChartCard>
        <ChartCard
          title="Fegyelmi — dolgozónként"
          subtitle="Top 10 dolgozó"
          icon={ShieldAlert}
          accent="#f97316"
        >
          <RankedList
            items={data.disciplinary?.by_employee || []}
            accent="#f97316"
            valueFormatter={(v) => `${fmtHu(v)} eset`}
          />
        </ChartCard>
      </div>
    </div>
  );
}

function OnboardingDial({ done, pending, total, pct }) {
  // SVG donut chart
  const r = 56;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="flex flex-col items-center gap-5 py-3 sm:flex-row sm:items-center sm:gap-7">
      <div className="relative h-[160px] w-[160px] shrink-0">
        <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
          <circle
            cx="80"
            cy="80"
            r={r}
            fill="none"
            stroke="rgba(15,23,42,0.06)"
            strokeWidth="14"
          />
          <circle
            cx="80"
            cy="80"
            r={r}
            fill="none"
            stroke="#10b981"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            style={{ transition: "stroke-dasharray 600ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <p className="font-mono text-[28px] font-light tabular-nums leading-none">
              {pct}%
            </p>
            <p className="mt-1 text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
              Kész
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-3">
        <DialRow color="#10b981" label="Lezárva" value={done} total={total} />
        <DialRow
          color="#f59e0b"
          label="Folyamatban"
          value={pending}
          total={total}
        />
        <div className="border-t border-slate-100 pt-2 dark:border-border">
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
            Összesen aktív
          </p>
          <p className="font-mono text-[18px] font-light tabular-nums">
            {fmtHu(total)}
          </p>
        </div>
      </div>
    </div>
  );
}

function DialRow({ color, label, value, total }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[12px]">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: color }}
          />
          <span className="font-medium">{label}</span>
        </span>
        <span className="font-mono tabular-nums text-slate-600">
          {fmtHu(value)}
          <span className="ml-1 text-slate-400">({pct}%)</span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-foreground/[0.06]">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

function DisciplinaryDetail({ data }) {
  if (!data) return <EmptyChart />;
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-4 dark:border-border dark:bg-foreground/[0.02]">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          Bejegyzés
        </p>
        <p className="mt-1.5 font-mono text-[24px] font-light leading-none tabular-nums">
          {fmtHu(data.total)}
        </p>
      </div>
      <div className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-4 dark:border-border dark:bg-foreground/[0.02]">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          Veszteség
        </p>
        <p
          className="mt-1.5 font-mono text-[24px] font-light leading-none tabular-nums"
          style={{ color: "#ef4444" }}
        >
          {fmtFt(data.lost_amount)}
        </p>
      </div>
      <div className="col-span-2 rounded-2xl border border-slate-200/80 bg-white p-3 dark:border-border dark:bg-card">
        <div className="flex items-center justify-between text-[12px]">
          <span className="text-slate-500">Elutasított bónusz</span>
          <span className="font-mono tabular-nums">
            {fmtHu(data.lost_bonus_count)} db
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[12px]">
          <span className="text-slate-500">Elutasított jutalék</span>
          <span className="font-mono tabular-nums">
            {fmtHu(data.lost_commission_count)} db
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- SOCIAL TAB ----------
function SocialTab({ data, period, loading }) {
  if (loading) return <SkeletonGrid />;
  if (!data) return <Empty />;
  const bucketLabel = period?.bucket_mode === "day" ? "naponta" : "havonta";
  const avg = data.shoots_top_influencers?.length
    ? Math.round(data.shoots_total / data.shoots_top_influencers.length)
    : 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          label="Összes forgatás"
          value={fmtHu(data.shoots_total)}
          sub="az időszakban"
          icon={Camera}
          accent="#a855f7"
        />
        <KpiTile
          label="Aktív influenszer"
          value={fmtHu(data.shoots_top_influencers?.length || 0)}
          sub="rögzített forgatással"
          icon={Star}
          accent="#ec4899"
        />
        <KpiTile
          label="Átlag / influenszer"
          value={fmtHu(avg)}
          sub="forgatás darab"
          icon={TrendingUp}
          accent="#6366f1"
        />
      </div>

      <ChartCard
        title="Forgatások trendje"
        subtitle={`Forgatások száma ${bucketLabel}`}
        icon={Camera}
        accent="#a855f7"
        full
      >
        <ShootsTrendChart data={data.shoots_series} />
      </ChartCard>

      <ChartCard
        title="Forgatások influenszerenként"
        subtitle="Top 12 — darabszám"
        icon={Star}
        accent="#ec4899"
        full
      >
        <RankedList
          items={data.shoots_top_influencers}
          accent="#ec4899"
          valueFormatter={(v) => `${fmtHu(v)} forgatás`}
        />
      </ChartCard>
    </div>
  );
}

function ShootsTrendChart({ data, height = 260 }) {
  if (!data?.length || data.every((d) => !d.count)) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="shootsGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="rgba(15,23,42,0.06)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 10.5, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={36}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            fontSize: 12,
            boxShadow: "0 12px 30px -10px rgba(15,23,42,0.2)",
          }}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="#a855f7"
          strokeWidth={2.5}
          fill="url(#shootsGrad)"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---------- Loading / Empty ----------
function SkeletonGrid() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[88px] animate-pulse rounded-2xl border border-slate-200/70 bg-slate-50/40 dark:border-border dark:bg-foreground/[0.02]"
          />
        ))}
      </div>
      <div className="h-[320px] animate-pulse rounded-3xl border border-slate-200/70 bg-slate-50/40 dark:border-border dark:bg-foreground/[0.02]" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-[260px] animate-pulse rounded-3xl border border-slate-200/70 bg-slate-50/40 dark:border-border dark:bg-foreground/[0.02]"
          />
        ))}
      </div>
    </div>
  );
}

function Empty() {
  return (
    <p className="rounded-3xl border border-dashed border-slate-200/80 bg-slate-50/40 px-6 py-12 text-center text-[13px] text-slate-500 dark:border-border dark:bg-foreground/[0.02]">
      Nincs megjeleníthető adat.
    </p>
  );
}


// ===========================================================================
// ScheduleTab — Beosztás statisztika
// ===========================================================================
function ScheduleTab({ data, loading }) {
  if (loading) return <SkeletonGrid />;
  if (!data || (data.shift_count === 0 && (data.by_employee || []).length === 0)) {
    return <Empty />;
  }
  return (
    <div className="space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ScheduleKpi
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Összes óra"
          value={`${fmtHu(data.total_hours)} h`}
          tone="indigo"
        />
        <ScheduleKpi
          icon={<CalendarRange className="h-3.5 w-3.5" />}
          label="Műszak"
          value={fmtHu(data.shift_count)}
          tone="slate"
        />
        <ScheduleKpi
          icon={<Users className="h-3.5 w-3.5" />}
          label="Dolgozó"
          value={fmtHu((data.by_employee || []).length)}
          tone="emerald"
        />
        <ScheduleKpi
          icon={<MapPin className="h-3.5 w-3.5" />}
          label="Telephely"
          value={fmtHu((data.by_site || []).length)}
          tone="amber"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ScheduleBreakdownCard
          title="Cégenként"
          icon={<Building2 className="h-3.5 w-3.5" />}
          rows={(data.by_company || []).map((r) => ({
            id: r.company_id,
            primary: r.company_name,
            secondary: `${fmtHu(r.sites)} telephely · ${fmtHu(r.employees)} dolgozó`,
            hours: r.hours,
            shifts: r.shifts,
          }))}
          total={data.total_hours}
          emptyText="Nincs céghez rendelt műszak."
          testId="stats-by-company"
        />
        <ScheduleBreakdownCard
          title="Telephelyenként"
          icon={<MapPin className="h-3.5 w-3.5" />}
          rows={(data.by_site || []).map((r) => ({
            id: r.site_id,
            primary: r.site_name,
            secondary: `${fmtHu(r.employees)} dolgozó · ${fmtHu(r.shifts)} műszak`,
            hours: r.hours,
            shifts: r.shifts,
          }))}
          total={data.total_hours}
          emptyText="Nincs telephely az időszakban."
          testId="stats-by-site"
        />
        <ScheduleBreakdownCard
          title="Dolgozónként"
          icon={<Users className="h-3.5 w-3.5" />}
          rows={(data.by_employee || []).map((r) => ({
            id: r.employee_id,
            primary: r.employee_name,
            secondary: `${fmtHu(r.shifts)} műszak`,
            hours: r.hours,
            shifts: r.shifts,
          }))}
          total={data.total_hours}
          emptyText="Nincs dolgozó az időszakban."
          testId="stats-by-employee"
        />
        <ScheduleBreakdownCard
          title="Telephely + Dolgozó"
          icon={<UserCheck className="h-3.5 w-3.5" />}
          rows={(data.by_site_employee || []).map((r) => ({
            id: `${r.site_id}-${r.employee_id}`,
            primary: r.employee_name,
            secondary: r.site_name,
            hours: r.hours,
            shifts: r.shifts,
          }))}
          total={data.total_hours}
          emptyText="Nincs adat."
          testId="stats-by-site-employee"
        />
      </div>
    </div>
  );
}

function ScheduleKpi({ icon, label, value, tone = "slate" }) {
  const toneClass = {
    indigo: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    slate: "bg-slate-100 text-slate-700 dark:bg-foreground/[0.06] dark:text-foreground",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white px-4 py-3.5 shadow-[0_1px_3px_0_rgba(0,0,0,0.03)] dark:border-border dark:bg-card">
      <span className={"inline-flex h-7 w-7 items-center justify-center rounded-lg " + toneClass}>
        {icon}
      </span>
      <p className="mt-2 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>
      <p className="mt-0.5 font-mono text-[20px] font-semibold tabular-nums tracking-tight">
        {value}
      </p>
    </div>
  );
}

function ScheduleBreakdownCard({ title, icon, rows, total, emptyText, testId }) {
  const max = rows.reduce((m, r) => Math.max(m, r.hours || 0), 0);
  const totalShown = rows.reduce((s, r) => s + (r.hours || 0), 0);
  return (
    <section
      data-testid={testId}
      className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.03)] dark:border-border dark:bg-card"
    >
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3 dark:border-border/60">
        <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <span className="grid h-6 w-6 place-items-center rounded-md bg-foreground/[0.05] text-slate-500">
            {icon}
          </span>
          {title}
        </h3>
        <p className="font-mono text-[11.5px] tabular-nums text-slate-500">
          {fmtHu(totalShown)} h
        </p>
      </header>
      <div className="max-h-[440px] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="px-5 py-8 text-center text-[12px] text-slate-400">{emptyText}</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-border/60">
            {rows.map((r) => {
              const pct = max > 0 ? (r.hours / max) * 100 : 0;
              const shareOfTotal = total > 0 ? ((r.hours / total) * 100) : 0;
              return (
                <li
                  key={r.id}
                  className="relative px-5 py-3"
                  data-testid={`${testId}-row-${r.id}`}
                >
                  {/* Background bar */}
                  <div
                    className="absolute inset-y-2 left-3 right-3 rounded-md bg-indigo-50/50 dark:bg-indigo-500/[0.07]"
                    style={{ width: `calc(${pct}% - 24px)` }}
                    aria-hidden
                  />
                  <div className="relative flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold tracking-tight">
                        {r.primary || "—"}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">
                        {r.secondary}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[14px] font-semibold tabular-nums">
                        {fmtHu(r.hours)}
                        <span className="ml-0.5 text-[10px] font-medium text-slate-400">h</span>
                      </p>
                      <p className="font-mono text-[10px] tabular-nums text-slate-400">
                        {shareOfTotal.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
